export const VIEW_W = 960;
export const VIEW_H = 540;

export const PLANET = {
  id: 'bluegrass_major',
  displayName: 'Bluegrass Major',
  radius: 144000,
  surfaceStroke: 26,
  atmosphere: 0,
  centerX: 0,
  centerY: 0,
  startAngle: -Math.PI / 2,
} as const;

export const PLAYER = {
  height: 68,
  headRadius: 8,
  footRadius: 3,
  runAcceleration: 980,
  groundFriction: 11.5,
  airAcceleration: 350,
  airDrag: 0.55,
  maxGroundSpeed: 248,
  maxAirSpeed: 214,
  maxSwimSpeed: 82,
  jumpVelocity: 610,
  gravity: 980,
  risingHoldGravityScale: 0.52,
  fallingGravityScale: 0.78,
  maxFallSpeed: 720,
  coyoteTicks: 6,
  jumpBufferTicks: 7,
  landingSquashTicks: 9,
  walkCyclePerPixel: 0.011,
  longIdleTicks: 260,
} as const;

export const CAMERA = {
  defaultZoom: 0.38,
  minZoom: 0.16,
  maxZoom: 0.82,
  zoomStep: 0.055,
  playerScreenY: 0.42,
  deadzoneHalfWidthPx: 170,
} as const;

export const WORLD_RULES = {
  homeSurfaceDistance: 0,
  actionRadius: 85,
  leftDiscoveryDistance: -760,
  rightDiscoveryDistance: 820,
  waterStartDistance: -1480,
  waterEndDistance: -4100,
  forestStartDistance: 1640,
  swimStaminaMax: 100,
  swimStaminaDrainPerSecond: 18,
  swimStaminaRecoverPerSecond: 32,
  homeReturnTicks: 120,
  blockedTicks: 42,
} as const;

export const WORLD = {
  w: PLANET.radius * 2 + 1800,
  h: PLANET.radius * 2 + 1800,
} as const;

export const SPRITE_IDS = ['vector_stick_player', 'planet_surface', 'minimap_player'] as const;
export type SpriteId = (typeof SPRITE_IDS)[number];

export type SurfaceFeatureId = 'left_learning_stone' | 'right_minimap_stone' | 'left_water' | 'right_forest';
export type SurfaceFeatureKind = 'action' | 'water' | 'forest';

export interface SurfaceFeatureConfig {
  id: SurfaceFeatureId;
  kind: SurfaceFeatureKind;
  label: string;
  distance: number;
  endDistance?: number;
  unlocks?: 'minimap';
}

export const SURFACE_FEATURES: readonly SurfaceFeatureConfig[] = [
  {
    id: 'left_learning_stone',
    kind: 'action',
    label: 'A quiet place to learn',
    distance: WORLD_RULES.leftDiscoveryDistance,
  },
  {
    id: 'right_minimap_stone',
    kind: 'action',
    label: 'You notice the shape of home',
    distance: WORLD_RULES.rightDiscoveryDistance,
    unlocks: 'minimap',
  },
  {
    id: 'left_water',
    kind: 'water',
    label: 'Wide water',
    distance: WORLD_RULES.waterStartDistance,
    endDistance: WORLD_RULES.waterEndDistance,
  },
  {
    id: 'right_forest',
    kind: 'forest',
    label: 'Dense forest',
    distance: WORLD_RULES.forestStartDistance,
  },
] as const;

export type TunableStatId =
  | 'planet.radius'
  | 'player.run_acceleration'
  | 'player.max_ground_speed'
  | 'player.jump_velocity'
  | 'player.gravity'
  | 'camera.default_zoom'
  | 'camera.deadzone_half_width_px'
  | 'water.swim_stamina_max';

export interface TunableStat {
  id: TunableStatId;
  owner: 'domain';
  value: number;
  intent: string;
}

export const TUNABLE_STATS: readonly TunableStat[] = [
  {
    id: 'planet.radius',
    owner: 'domain',
    value: PLANET.radius,
    intent: 'Large enough that the platform reads as almost flat until the player zooms out slightly.',
  },
  {
    id: 'player.run_acceleration',
    owner: 'domain',
    value: PLAYER.runAcceleration,
    intent: 'Responsive left/right control along the local tangent without arcade teleporting.',
  },
  {
    id: 'player.max_ground_speed',
    owner: 'domain',
    value: PLAYER.maxGroundSpeed,
    intent: 'Fast enough to animate the legs clearly while keeping the curve readable.',
  },
  {
    id: 'player.jump_velocity',
    owner: 'domain',
    value: PLAYER.jumpVelocity,
    intent: 'Floaty, tall Luigi-like jump arc rather than a short heavy hop.',
  },
  {
    id: 'player.gravity',
    owner: 'domain',
    value: PLAYER.gravity,
    intent: 'Radial pull toward the planet center; scaled during jump hold and falling.',
  },
  {
    id: 'camera.default_zoom',
    owner: 'domain',
    value: CAMERA.defaultZoom,
    intent: 'Initial view shows the player small enough to almost see the surface curve.',
  },
  {
    id: 'camera.deadzone_half_width_px',
    owner: 'domain',
    value: CAMERA.deadzoneHalfWidthPx,
    intent: 'Normal platformer camera window before the world follows east or west.',
  },
  {
    id: 'water.swim_stamina_max',
    owner: 'domain',
    value: WORLD_RULES.swimStaminaMax,
    intent: 'Soft early boundary: swimming is allowed, but stamina returns the player home before crossing.',
  },
];
